import { useState } from 'react';
import { useBrand } from '../providers/BrandProvider';
import { useTheme } from '../providers/ThemeProvider';

/**
 * The official O.T.E Connect logo (DESIGN_SYSTEM.md §9, PRD §3.12).
 *
 * - `mark`: letters + arc + dot. Sidebar and mobile top bar; 36 px high.
 * - `lockup`: mark + "connect". Login and portal header; ≥ 96 px high.
 * - `symbol`: arc + o + dot. Collapsed 72 px rail; 32 px wide.
 *
 * The files are exact exports (public/brand/, never redrawn). Always rendered
 * as <img>, never inlined. The dark appearance (T-53) picks the official white
 * variant (`-white` files, DESIGN_SYSTEM.md §9; never a CSS filter). `tone`:
 * omitted = follow the theme; `onDark` / `onLight` = a fixed surface;
 * `onInk` = on the ink fill (`--c-text`: white file in light, ink file in dark,
 * e.g. the QuickCreate button). Sizes are
 * fixed at the minimums; pass `className` only for layout (margins), or a
 * larger height for the lockup.
 */
export type BrandLogoVariant = 'mark' | 'lockup' | 'symbol';

/** @deprecated v1 names, kept until every caller moves to `mark` / `lockup`. */
type LegacyVariant = 'sidebar' | 'mobile' | 'login' | 'login-dark';

interface BrandLogoProps {
  variant: BrandLogoVariant | LegacyVariant;
  tone?: 'onLight' | 'onDark' | 'onInk';
  className?: string;
  /** Decorative when the product name is already written next to it. */
  decorative?: boolean;
  /** Fill the parent's width instead of the fixed minimum height (the parent sets a size ≥ the minimum). */
  fill?: boolean;
}

// v1 names: the light ones sat on app surfaces, so they follow the theme (no fixed tone).
const LEGACY: Record<LegacyVariant, { variant: BrandLogoVariant; tone?: 'onDark' }> = {
  sidebar: { variant: 'mark' },
  mobile: { variant: 'mark' },
  login: { variant: 'lockup' },
  'login-dark': { variant: 'lockup', tone: 'onDark' },
};

// Minimum sizes (PRD §3.12): mark 36 px high, lockup 96 px high, symbol 32 px wide.
const SIZE: Record<BrandLogoVariant, string> = {
  mark: 'h-9 w-auto',
  lockup: 'h-24 w-auto',
  symbol: 'w-8 h-auto',
};

const FALLBACK_SIZE: Record<BrandLogoVariant, string> = {
  mark: 'h-9 w-9 text-base',
  lockup: 'h-24 w-24 text-4xl',
  symbol: 'h-8 w-8 text-sm',
};

export default function BrandLogo({ variant, tone, className = '', decorative = false, fill = false }: BrandLogoProps) {
  const brand = useBrand();
  const { resolved } = useTheme();
  const [imgError, setImgError] = useState(false);

  const legacy = variant in LEGACY ? LEGACY[variant as LegacyVariant] : null;
  const kind: BrandLogoVariant = legacy ? legacy.variant : (variant as BrandLogoVariant);
  const want = tone ?? legacy?.tone;
  const dark = resolved === 'dark';
  const onDark = want === 'onDark' || (want === 'onInk' ? !dark : want !== 'onLight' && dark);

  const src = {
    mark: onDark ? brand.logos.markOnDark : brand.logos.mark,
    lockup: onDark ? brand.logos.lockupOnDark : brand.logos.lockup,
    symbol: onDark ? brand.logos.symbolOnDark : brand.logos.symbol,
  }[kind];

  if (imgError || !src) {
    // White-label tenant without logo files: initial-letter tile.
    const initial = brand.productName.charAt(0).toUpperCase();
    return (
      <div
        className={`${FALLBACK_SIZE[kind]} rounded-lg flex items-center justify-center font-semibold bg-accent text-on-accent ${className}`}
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : brand.productName}
        aria-hidden={decorative || undefined}
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={decorative ? '' : brand.productName}
      className={`${fill ? 'block w-full h-auto' : `${SIZE[kind]} max-w-none`} object-contain select-none ${className}`}
      draggable={false}
      onError={() => setImgError(true)}
    />
  );
}
